import { datadogRum } from '@datadog/browser-rum';
import { useVisibilityChange } from '@uidotdev/usehooks';
import { WebSocket as ReconnectingWebSocket } from 'partysocket';
import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAccessToken } from '../auth/use-access-token';
import type { ConnectionStatus, InboundWsAction, Send, Subscribe } from './messages';
import { INBOUND_WS_ACTIONS } from './messages';

type InternalHandler = (raw: Record<string, unknown>) => void;

const INBOUND_ACTIONS: ReadonlySet<InboundWsAction> = new Set(INBOUND_WS_ACTIONS);

const isInboundAction = (value: unknown): value is InboundWsAction =>
  typeof value === 'string' && INBOUND_ACTIONS.has(value as InboundWsAction);

const WS_URL = import.meta.env.VITE_WS_URL;

// How long a ping may go unanswered before the socket is presumed dead.
// readyState reports the WebSocket close handshake, not whether the connection
// is alive, so a blackholed socket answers OPEN indefinitely — the pong is the
// only thing that turns "it says it's open" into evidence that it is.
//
// Unlike the Companion (ORD-432), this enforces from the very first ping rather
// than waiting to see one pong. The Companion needed that guard because it
// auto-updates and could reach a store before the API Gateway config did; this
// app is served fresh on every load and only ever talks to the one deployment
// that already answers. Waiting would also blind the foreground check below,
// which is often a connection's first ping and is the one that matters most.
const PONG_DEADLINE_MS = 10 * 1000;

// How often the keepalive pings once a connection is open. Together with
// PONG_DEADLINE_MS this bounds how long a dead-but-OPEN socket can linger in
// a foregrounded tab; a backgrounded tab throttles timers anyway, so the
// visibility nudge below is what checks the socket the moment it matters.
const KEEPALIVE_INTERVAL_MS = 5 * 60 * 1000;

export type StoreWebSocketContextValue = {
  subscribe: Subscribe;
  send: Send;
  // Ping the open socket and hold it to answering (reconnect on a missed
  // pong). For a consumer that has just seen evidence the network may be gone
  // — a write the server never answered — and should not wait for the next
  // keepalive to find out.
  checkLiveness: () => void;
  // Replace a closed socket now, instead of letting partysocket sleep out its
  // backoff. For a consumer that has just seen the opposite evidence — the
  // server answered a request — while the chip may still read Offline.
  ensureConnected: () => void;
  status: {
    subscribe: (listener: () => void) => () => void;
    getSnapshot: () => ConnectionStatus;
  };
};

export const StoreWebSocketContext = createContext<StoreWebSocketContextValue | null>(null);

export interface StoreWebSocketProviderProps {
  storeId: string;
  children: ReactNode;
}

export function StoreWebSocketProvider({
  storeId,
  children,
}: StoreWebSocketProviderProps): ReactNode {
  const getAccessTokenSilently = useAccessToken();
  const socketRef = useRef<ReconnectingWebSocket | null>(null);

  const subscribersRef = useRef(new Map<InboundWsAction, Set<InternalHandler>>());
  const statusRef = useRef<ConnectionStatus>('closed');
  const statusListenersRef = useRef(new Set<() => void>());

  const pongTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const notifyStatus = useCallback((next: ConnectionStatus): void => {
    statusRef.current = next;
    for (const l of statusListenersRef.current) {
      l();
    }
  }, []);

  const clearPongDeadline = useCallback((): void => {
    if (pongTimerRef.current !== null) {
      clearTimeout(pongTimerRef.current);
      pongTimerRef.current = null;
    }
  }, []);

  // Ping, and hold the socket to answering. Both callers live outside the
  // connection effect — the keepalive interval and the foreground nudge — so
  // this sits at component scope where both can reach it and there is one
  // definition of what a liveness check is.
  const pingAndAwaitPong = useCallback((): void => {
    const sock = socketRef.current;
    if (!sock || sock.readyState !== ReconnectingWebSocket.OPEN) {
      return;
    }

    sock.send(JSON.stringify({ action: 'ping' }));

    // One deadline at a time — the newest ping's — so a slow connection
    // produces one reconnect rather than a queue of them.
    clearPongDeadline();
    pongTimerRef.current = setTimeout(() => {
      pongTimerRef.current = null;
      // Loud on purpose: a missed pong is a zombie connection detected, and
      // should be tellable apart from routine reconnects in RUM — the
      // Companion logs its equivalent for the same reason.
      datadogRum.addAction('websocket_pong_timeout');
      socketRef.current?.reconnect();
    }, PONG_DEADLINE_MS);
  }, [clearPongDeadline]);

  // Bumping this tears the socket down and builds a fresh one (the connection
  // effect lists it). It is how a closed socket is brought back NOW: partysocket
  // 1.3's reconnect() is a no-op while a retry is sleeping out its backoff —
  // _connect holds its lock through the whole wait — so on the ORD-458 re-test
  // the browser's `online` nudge did nothing and the chip read Offline until
  // the pending delay (up to 30 s) elapsed, long after the save had landed
  // (ORD-462). Replacing the instance is the same move the storeId change
  // makes, and the only one partysocket's public API leaves open.
  const [socketGeneration, setSocketGeneration] = useState(0);

  // Restore a socket that is between attempts. CLOSED is what partysocket
  // reports while sleeping out a backoff; a socket mid-handshake (CONNECTING)
  // is left to finish — replacing it would churn the initial connect every
  // time a save landed during it.
  const ensureConnected = useCallback((): void => {
    const sock = socketRef.current;
    if (!sock || sock.readyState !== ReconnectingWebSocket.CLOSED) {
      return;
    }

    setSocketGeneration((generation) => generation + 1);
  }, []);

  // The "is the socket still worth trusting?" move, shared by everything that
  // learns the network may have changed under it — the tab coming back to the
  // foreground, the browser reporting online. An open socket is checked (a
  // ping held to the pong deadline: readyState === OPEN is exactly what a
  // blackholed socket reports, so a bare ping was the branch a dead
  // connection always took); a closed one is replaced now instead of waiting
  // out partysocket's backoff.
  const nudge = useCallback((): void => {
    const sock = socketRef.current;
    if (!sock) {
      return;
    }

    if (sock.readyState === ReconnectingWebSocket.OPEN) {
      pingAndAwaitPong();
    } else {
      ensureConnected();
    }
  }, [pingAndAwaitPong, ensureConnected]);

  // `.parse()`, not `.safeParse()`: the catch below is what makes a rejected
  // frame stall silently and land in RUM instead of throwing into the socket's
  // message loop, and that routing is the behaviour worth keeping. Nor is there
  // a `{ strict: true }` to carry over from the Yup era — Zod does not coerce,
  // so the guard those schemas relied on is now the default.
  const subscribe = useCallback<Subscribe>((action, payloadSchema, handler) => {
    const wrapper: InternalHandler = (raw) => {
      try {
        const payload = payloadSchema.parse(raw);
        handler(payload);
      } catch (err) {
        datadogRum.addError(err, { ws_action: action, raw });
      }
    };

    let handlers = subscribersRef.current.get(action);
    if (!handlers) {
      handlers = new Set();
      subscribersRef.current.set(action, handlers);
    }

    handlers.add(wrapper);

    return () => {
      handlers?.delete(wrapper);
    };
  }, []);

  const send = useCallback<Send>((action, payloadSchema, payload) => {
    const sock = socketRef.current;
    if (!sock || sock.readyState !== ReconnectingWebSocket.OPEN) {
      console.log('[ws] send dropped (socket closed)', { action, payload });

      return 'dropped_closed';
    }

    let validated: unknown;
    try {
      validated = payloadSchema.parse(payload);
    } catch (err) {
      datadogRum.addError(err, { ws_action: action, payload });
      console.log('[ws] send dropped (invalid payload)', { action, payload, err });

      return 'dropped_invalid';
    }

    const wire = { ...(validated as object), action };
    sock.send(JSON.stringify(wire));
    console.log('[ws] sent', { action, payload: validated });

    return 'sent';
  }, []);

  const statusStore = useMemo(
    () => ({
      subscribe: (listener: () => void) => {
        statusListenersRef.current.add(listener);

        return () => {
          statusListenersRef.current.delete(listener);
        };
      },
      getSnapshot: () => statusRef.current,
    }),
    []
  );

  useEffect(() => {
    if (!WS_URL) {
      throw new Error(
        'VITE_WS_URL is not set. Add it to .env.local to enable WebSocket connections.'
      );
    }

    const buildUrl = async (): Promise<string> => {
      const token = await getAccessTokenSilently();

      // auth0's accessor is nullable since 2.26 (the session ceiling).
      // useAccessToken turns that case into a login redirect that never
      // resolves, so this is unreachable — but refuse loudly rather than open
      // a socket with `token=undefined` if that ever stops holding.
      // partysocket routes a rejected url provider into its error/retry path.
      if (!token) {
        throw new Error('no access token available for the store WebSocket');
      }

      const params = new URLSearchParams({
        token,
        store_id: storeId,
        client_type: 'web',
      });

      return `${WS_URL}?${params.toString()}`;
    };

    if (socketGeneration > 0) {
      // Loud like the pong timeout: a forced replacement is a connection that
      // was down when the user (or the network) came back, and should be
      // tellable apart from routine reconnects in RUM.
      datadogRum.addAction('websocket_forced_reconnect', { generation: socketGeneration });
    }

    notifyStatus('connecting');
    const rws = new ReconnectingWebSocket(buildUrl, [], {
      minReconnectionDelay: 1_000,
      maxReconnectionDelay: 30_000,
      reconnectionDelayGrowFactor: 2,
    });
    socketRef.current = rws;

    let keepaliveTimer: ReturnType<typeof setInterval> | null = null;

    const startKeepalive = (): void => {
      if (keepaliveTimer !== null) {
        return;
      }

      keepaliveTimer = setInterval(pingAndAwaitPong, KEEPALIVE_INTERVAL_MS);
    };

    const clearKeepalive = (): void => {
      if (keepaliveTimer !== null) {
        clearInterval(keepaliveTimer);
        keepaliveTimer = null;
      }

      // Always together: a deadline left armed across a reconnect would fire
      // against a socket it was never measuring.
      clearPongDeadline();
    };

    const handleOpen = (): void => {
      notifyStatus('open');
      // Ask the backend to reconcile our store data version: any store_updated
      // broadcast missed while this socket was down is recovered on reconnect.
      // Sent raw like the keepalive ping — both are low-level control frames
      // that bypass the validated send() wrapper.
      rws.send(JSON.stringify({ action: 'hello' }));
      startKeepalive();
    };

    const handleClose = (): void => {
      notifyStatus('closed');
      clearKeepalive();
    };

    const handleMessage = (event: MessageEvent): void => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        datadogRum.addError(new Error('websocket: JSON parse failure'), {
          ws_raw_data: typeof event.data === 'string' ? event.data.slice(0, 200) : '<non-string>',
        });

        return;
      }

      if (parsed === null || typeof parsed !== 'object' || !('action' in parsed)) {
        datadogRum.addError(new Error('websocket: missing action field'), {
          raw: parsed,
        });

        return;
      }

      const raw = parsed as Record<string, unknown>;
      const action = raw.action;

      // The pong answering our keepalive ping. Transport, not an application
      // action — the same reason `ping` is absent from INBOUND_WS_ACTIONS — so
      // it is consumed here rather than falling through to the unknown-action
      // branch, which filed a new RUM issue every five minutes in every open
      // tab. Not logged either: it says nothing a reader of the console wants.
      if (action === 'pong') {
        clearPongDeadline();

        return;
      }

      if (!isInboundAction(action)) {
        datadogRum.addError(new Error('websocket: unknown action'), {
          ws_action: action,
          raw,
        });

        return;
      }

      const { action: _ignored, ...payload } = raw;
      console.log('[ws] received', { action, payload });
      const handlers = subscribersRef.current.get(action);
      if (!handlers) {
        return;
      }

      for (const wrapper of handlers) {
        try {
          wrapper(payload);
        } catch (err) {
          console.error('StoreWebSocketProvider: handler threw', err);
        }
      }
    };

    // The browser's own verdict. Neither readyState nor the keepalive notices
    // the network going away — the socket reports OPEN until a ping goes
    // unanswered, up to five minutes later — so a save failing on a dead
    // network showed Saving… for minutes before the chip could say Offline
    // (Phase D drive, ORD-458). Report closed at once and let partysocket
    // start its backoff (reconnect() works here: the socket is open, so no
    // retry is sleeping yet); `online` is the nudge the foreground handler
    // already is, which replaces a socket caught mid-backoff.
    const handleOffline = (): void => {
      notifyStatus('closed');
      clearKeepalive();
      rws.reconnect();
    };

    rws.addEventListener('open', handleOpen);
    rws.addEventListener('close', handleClose);
    rws.addEventListener('message', handleMessage);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', nudge);

    return () => {
      rws.removeEventListener('open', handleOpen);
      rws.removeEventListener('close', handleClose);
      rws.removeEventListener('message', handleMessage);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', nudge);
      clearKeepalive();
      rws.close();
      socketRef.current = null;
      notifyStatus('closed');
    };
    // pingAndAwaitPong, clearPongDeadline and nudge are useCallbacks with
    // stable identities for the component's lifetime, so listing them cannot
    // cause this effect to tear down and rebuild the socket; socketGeneration
    // is the one dependency meant to.
  }, [
    storeId,
    socketGeneration,
    getAccessTokenSilently,
    notifyStatus,
    pingAndAwaitPong,
    clearPongDeadline,
    nudge,
  ]);

  // When the tab returns to the foreground, nudge the socket: if it's still
  // open, ping to reset API Gateway's idle timer (and any NAT entries) before
  // the user acts; if it died while backgrounded — API Gateway's 10-minute
  // idle timeout, or a frozen-tab timer throttle that stalled the keepalive —
  // replace it now instead of waiting out partysocket's backoff, so the
  // viewer lease is re-acquired in time. Skip the initial mount run: the
  // socket is still connecting then and there is nothing to nudge.
  const documentVisible = useVisibilityChange();
  const visibilityPrimedRef = useRef(false);
  useEffect(() => {
    if (!visibilityPrimedRef.current) {
      visibilityPrimedRef.current = true;

      return;
    }

    if (documentVisible) {
      nudge();
    }
  }, [documentVisible, nudge]);

  const contextValue = useMemo<StoreWebSocketContextValue>(
    () => ({
      subscribe,
      send,
      checkLiveness: pingAndAwaitPong,
      ensureConnected,
      status: statusStore,
    }),
    [subscribe, send, pingAndAwaitPong, ensureConnected, statusStore]
  );

  return (
    <StoreWebSocketContext.Provider value={contextValue}>{children}</StoreWebSocketContext.Provider>
  );
}
